import React, { lazy, Suspense, useEffect } from 'react'
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { initGA, trackPageView } from '../utils/googleAnalytics';

import * as ROUTES from '../routes'

import AuthorizedComponent from '../Components/AuthorizedComponent';

import configureStore from '../Redux/store'
import { withReduxAuth } from '../Redux/indexAuth';
import { showCover, hideCover, getCover } from '../utils/cover';

// Suspense fallback: makes the cover visible while lazy chunks load, fades it out on resolve.
function SuspenseCover() {
  useEffect(() => {
    getCover(); // ensure element exists
    showCover();
    return () => hideCover();
  }, []);
  return null;
}

// Lazy load route components for better initial load performance
const Main         = lazy(() => import('../Containers/Main'));
const Dashboard    = lazy(() => import('../Pages/Dashboard'));
const Project      = lazy(() => import('../Pages/Project'));
const User         = lazy(() => import('../Pages/User'));
const Unsubscribe  = lazy(() => import('../Pages/Unsubscribe'));
const Admin        = lazy(() => import('../Pages/Admin'));
const PrivacyPolicy   = lazy(() => import('../Pages/PrivacyPolicy'));
const TermsOfService  = lazy(() => import('../Pages/TermsOfService'));
const EarlyAccess     = lazy(() => import('../Pages/EarlyAccess'));

const store = configureStore();
// Google Analytics 4 initialization
initGA();
// Google Analytics Tracker
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);
  return null;
}

const AppRoutes: React.FC<any> = ({ auth }) => {
  return (
    <BrowserRouter future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }}>
      <RouteTracker />
      <Suspense fallback={<SuspenseCover />}>
        <Routes>
          <Route path={ROUTES.PATH.LANDING} element={<AuthorizedComponent element={<Main replace/>} />}>
            <Route path={ROUTES.PATH.LANDING} element={<Dashboard />} />
            {auth.gnUser?.userRole === 'gn_admin' && (
              <Route path={ROUTES.PATH.ADMIN} element={<Admin />} />
            )}
            <Route path={ROUTES.PATH.PROJECT_BASE} element={<Project />} />
            <Route path={ROUTES.PATH.USER_BASE} element={<User />} />
          </Route>
          <Route path={ROUTES.PATH.EARLY_ACCESS} element={<EarlyAccess/>} />
          <Route path={ROUTES.PATH.UNSUBSCRIBE} element={<Unsubscribe />} />
          <Route path={ROUTES.PATH.TERMS_OF_SERVICE} element={<TermsOfService />} />
          <Route path={ROUTES.PATH.PRIVACY_POLICY} element={<PrivacyPolicy />} />
          <Route path="*" element={<AuthorizedComponent element={<Main replace/>} />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

const ConnectedAppRoutes = withReduxAuth(AppRoutes);

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <ConnectedAppRoutes />
    </Provider>
  )
}

export default App

